/**
 * THEME-1 · 主题同步与读取：
 * · `ThemeSync`（挂在应用根）：登录前用公开品牌出口带出的令牌换色；登录后读 GET /api/v1/settings/theme（令牌 + 后台版式），写缓存。
 * · `useAdminLayout()`：后台版式 sidebar / topnav（取不到 = 缓存 = 默认 sidebar）。
 * · `useThemeSettings()`：主题设置整份（设置页用）。
 * · `useEdition()`：版本类型 enterprise 企业版（默认）/ subscription 订阅版（SAAS-1；订阅版专属的页面 / 卡片都靠它判断）。
 */
import { useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuthStore } from "@/store/authStore";
import { settingsApi, type ThemeSettings } from "@/services/settings";
import { usePublicBranding } from "@/shared/branding";
import { applyTheme, readCache, writeCache, type AdminLayout } from "./applyTheme";

export const THEME_QK = ["settings", "theme"] as const;

export type Edition = "enterprise" | "subscription";
const EDITION_KEY = "ota-edition-v1";

function readEditionCache(): Edition {
  try {
    return localStorage.getItem(EDITION_KEY) === "subscription" ? "subscription" : "enterprise";
  } catch {
    return "enterprise";
  }
}
function writeEditionCache(e: Edition) {
  try {
    localStorage.setItem(EDITION_KEY, e);
  } catch {
    /* 隐私模式：不缓存也照常用 */
  }
}

export function useThemeSettings() {
  const authed = useAuthStore((s) => !!s.accessToken);
  return useQuery<ThemeSettings>({ queryKey: THEME_QK, queryFn: settingsApi.getTheme, enabled: authed, staleTime: 60_000, retry: 1 });
}

/** 版本类型：先用缓存（首帧就对，不闪一下企业版首页），/theme 回来后以后端为准；没设置 = enterprise。 */
export function useEdition(): Edition {
  const q = useThemeSettings();
  return q.data?.edition ?? readEditionCache();
}

/** 后台版式：先用缓存（首帧就对），/theme 回来后以后端为准。 */
export function useAdminLayout(): AdminLayout {
  const q = useThemeSettings();
  return q.data?.admin_layout ?? readCache().admin_layout;
}

export function ThemeSync() {
  const authed = useAuthStore((s) => !!s.accessToken);
  const branding = usePublicBranding();
  const theme = useThemeSettings();

  // 登录前（或 /theme 还没回来）：公开出口带出的令牌
  useEffect(() => {
    if (!authed && branding?.theme) applyTheme(branding.theme.tokens);
  }, [authed, branding?.theme]);

  // 登录后：以后端 /theme 为准，并写缓存（下次首帧就对）
  useEffect(() => {
    if (!authed || !theme.data) return;
    applyTheme(theme.data.tokens);
    writeCache({ tokens: theme.data.tokens, admin_layout: theme.data.admin_layout });
    writeEditionCache(theme.data.edition ?? "enterprise");
  }, [authed, theme.data]);
  return null;
}

/** 保存主题后调：立即刷新缓存 / 页面（不等 60 秒）。 */
export function useRefreshTheme() {
  const qc = useQueryClient();
  return (next: ThemeSettings) => {
    qc.setQueryData(THEME_QK, next);
    applyTheme(next.tokens);
    writeCache({ tokens: next.tokens, admin_layout: next.admin_layout });
    writeEditionCache(next.edition ?? "enterprise");
  };
}
